// src/pages/vendor/VendorTransactions.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { vendorApi } from "../../api/vendorApi";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Refresh,
  Search,
  Visibility,
  Receipt,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Payment as PaymentIcon,
  CheckCircle,
  Cancel,
  Pending,
  Download,
  Print,
  Clear,
  LocalAtm,
  Storefront,
} from "@mui/icons-material";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`transactions-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TRANSACTION_TYPES = [
  { value: "all", label: "All Types" },
  { value: "deposit", label: "Deposit" },
  { value: "withdrawal", label: "Withdrawal" },
  { value: "payment", label: "Payment" },
  { value: "payout", label: "Payout" },
  { value: "refund", label: "Refund" },
  { value: "commission", label: "Commission" },
  { value: "order_payment", label: "Order Payment" },
];

const TRANSACTION_STATUS = [
  { value: "all", label: "All Status" },
  { value: "pending", label: "Pending", color: "#ff9800" },
  { value: "processing", label: "Processing", color: "#2196f3" },
  { value: "success", label: "Success", color: "#4caf50" },
  { value: "failed", label: "Failed", color: "#f44336" },
  { value: "cancelled", label: "Cancelled", color: "#9e9e9e" },
];

const getStatusChip = (status) => {
  const safeStatus = status?.toString()?.toLowerCase() || 'pending';
  const config = {
    pending: { color: "warning", icon: <Pending sx={{ fontSize: 14 }} /> },
    processing: { color: "info", icon: <CircularProgress size={14} /> },
    success: { color: "success", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    paid: { color: "success", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    completed: { color: "success", icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    failed: { color: "error", icon: <Cancel sx={{ fontSize: 14 }} /> },
    cancelled: { color: "default", icon: <Cancel sx={{ fontSize: 14 }} /> },
  };
  const cfg = config[safeStatus] || config.pending;
  return (
    <Chip
      label={safeStatus?.toUpperCase() || "PENDING"}
      color={cfg.color}
      size="small"
      icon={cfg.icon}
    />
  );
};

const getTypeIcon = (type) => {
  const icons = {
    deposit: <TrendingUp sx={{ color: "#4caf50" }} />,
    withdrawal: <TrendingDown sx={{ color: "#f44336" }} />,
    payment: <PaymentIcon sx={{ color: "#2196f3" }} />,
    payout: <LocalAtm sx={{ color: "#ff9800" }} />,
    refund: <TrendingUp sx={{ color: "#9e9e9e" }} />,
    commission: <Receipt sx={{ color: "#9c27b0" }} />,
    order_payment: <Storefront sx={{ color: "#2196f3" }} />,
  };
  return icons[type] || <Receipt />;
};

const VendorTransactions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    limit: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    startDate: "",
    endDate: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalRevenue: 0,
    totalPayouts: 0,
    pendingPayouts: 0,
    totalCommission: 0,
  });
  const [tabValue, setTabValue] = useState(0);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        search: filters.search || undefined,
        type: filters.type !== "all" ? filters.type : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      };

      const response = await vendorApi.getTransactions(params);
      const data = response?.data || response || {};
      const transactionsList = data.transactions || data.data || [];
      const paginationData = data.pagination || data.meta || {};

      setTransactions(Array.isArray(transactionsList) ? transactionsList : []);
      setPagination({
        page: paginationData.page ? paginationData.page - 1 : 0,
        limit: paginationData.limit || 20,
        total: paginationData.total || 0,
      });

      if (data.stats) {
        setStats({
          totalTransactions: data.stats.total || 0,
          totalRevenue: data.stats.totalRevenue || 0,
          totalPayouts: data.stats.totalPayouts || 0,
          pendingPayouts: data.stats.pendingPayouts || 0,
          totalCommission: data.stats.totalCommission || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching vendor transactions:", error);
      setError(error?.response?.data?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handlePageChange = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (event) => {
    setPagination((prev) => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      type: "all",
      status: "all",
      startDate: "",
      endDate: "",
    });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailDialogOpen(true);
  };

  const formatCurrency = (amount) => {
  const numAmount = parseFloat(amount) || 0;
  return `ETB ${numAmount.toFixed(2)}`;
};

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading && transactions.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 10, sm: 12 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              My Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View your transaction history, payments, and payouts
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTransactions}
              disabled={loading}
              size={isMobile ? "small" : "medium"}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              size={isMobile ? "small" : "medium"}
              onClick={() => {}}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Revenue
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {formatCurrency(stats.totalRevenue)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Payouts
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(stats.totalPayouts)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Pending Payouts
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="warning.main">
                {formatCurrency(stats.pendingPayouts)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search transactions..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: filters.search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange("search", "")}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange("type", e.target.value)}
                label="Type"
              >
                {TRANSACTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                label="Status"
              >
                {TRANSACTION_STATUS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label="All Transactions" />
          <Tab label="Payments" />
          <Tab label="Payouts" />
          <Tab label="Commissions" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <TransactionTable
          transactions={transactions}
          pagination={pagination}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          handleViewDetails={handleViewDetails}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusChip={getStatusChip}
          getTypeIcon={getTypeIcon}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TransactionTable
          transactions={transactions.filter(tx => 
            tx.type === 'payment' || tx.type === 'order_payment'
          )}
          pagination={pagination}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          handleViewDetails={handleViewDetails}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusChip={getStatusChip}
          getTypeIcon={getTypeIcon}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <TransactionTable
          transactions={transactions.filter(tx => 
            tx.type === 'payout' || tx.type === 'withdrawal'
          )}
          pagination={pagination}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          handleViewDetails={handleViewDetails}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusChip={getStatusChip}
          getTypeIcon={getTypeIcon}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <TransactionTable
          transactions={transactions.filter(tx => tx.type === 'commission')}
          pagination={pagination}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          handleViewDetails={handleViewDetails}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
          getStatusChip={getStatusChip}
          getTypeIcon={getTypeIcon}
        />
      </TabPanel>

      {/* Transaction Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedTransaction && (
          <>
            <DialogTitle>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h6">
                  Transaction #{selectedTransaction.id || "N/A"}
                </Typography>
                {getStatusChip(selectedTransaction.status)}
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {selectedTransaction.type?.replace("_", " ") || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(selectedTransaction.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransaction.paymentMethod ||
                      selectedTransaction.bank_type ||
                      "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Transaction Reference
                  </Typography>
                  <Typography variant="body1" fontSize="12px" sx={{ wordBreak: "break-all" }}>
                    {selectedTransaction.transactionId ||
                      selectedTransaction.external_id ||
                      "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.createdAt || selectedTransaction.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Updated At
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedTransaction.updatedAt || selectedTransaction.updated_at)}
                  </Typography>
                </Grid>
                {selectedTransaction.description && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {selectedTransaction.description}
                    </Typography>
                  </Grid>
                )}
                {selectedTransaction.orderId && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Related Order
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setDetailDialogOpen(false);
                        navigate(`/vendor/orders/${selectedTransaction.orderId}`);
                      }}
                    >
                      View Order #{selectedTransaction.orderId}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
              <Button variant="contained" startIcon={<Print />}>
                Print
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// Transaction Table Component
const TransactionTable = ({
  transactions,
  pagination,
  handlePageChange,
  handleLimitChange,
  handleViewDetails,
  formatCurrency,
  formatDate,
  getStatusChip,
  getTypeIcon,
}) => {
  if (transactions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
        <Typography color="text.secondary">No transactions found</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow key={tx.id || tx.transactionId} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="500">
                    #{tx.id || tx.transactionId || "N/A"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {getTypeIcon(tx.type)}
                    <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                      {tx.type?.replace("_", " ") || "N/A"}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={
                      tx.type === "deposit" ||
                      tx.type === "payment" ||
                      tx.type === "order_payment" ||
                      tx.type === "transfer_received"
                        ? "success.main"
                        : tx.type === "commission"
                        ? "text.primary"
                        : "error.main"
                    }
                  >
                    {tx.type === "deposit" ||
                    tx.type === "payment" ||
                    tx.type === "order_payment" ||
                    tx.type === "transfer_received"
                      ? "+"
                      : tx.type === "commission"
                      ? ""
                      : "-"}
                    {formatCurrency(tx.amount)}
                  </Typography>
                  {tx.type === "commission" && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      ({tx.commissionRate || 0}% commission)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{getStatusChip(tx.status)}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontSize="12px">
                    {formatDate(tx.createdAt || tx.created_at)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetails(tx)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page}
        rowsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
    </Paper>
  );
};

export default VendorTransactions;